import { c as _c } from "react/compiler-runtime";
import React from "react";
import NextImage from "next/image";
import { cva, type VariantProps } from "class-variance-authority";
import { focalPointToPosition, type FocalPoint } from "@/lib/utils/focalPoint";
import { LightboxImage } from "./lightbox/LightboxImage";
const imageVariants = cva("", {
  variants: {
    objectFit: {
      cover: "object-cover",
      contain: "object-contain",
      fill: "object-fill",
      none: "object-none",
      scaleDown: "object-scale-down"
    },
    objectPosition: {
      center: "object-center",
      top: "object-top",
      bottom: "object-bottom",
      left: "object-left",
      right: "object-right"
    },
    rounded: {
      none: "rounded-none",
      sm: "rounded-sm",
      md: "rounded-md",
      lg: "rounded-lg",
      xl: "rounded-xl",
      full: "rounded-full"
    }
  },
  defaultVariants: {
    objectFit: "cover",
    objectPosition: "center",
    rounded: "none"
  }
});
export interface ImageProps extends VariantProps<typeof imageVariants> {
  /**
   * Image source URL or path
   */
  src: string;

  /**
   * Alternative text for accessibility (required)
   */
  alt: string;

  /**
   * Image width in pixels
   * Required unless using fill layout
   */
  width?: number;

  /**
   * Image height in pixels
   * Required unless using fill layout
   */
  height?: number;

  /**
   * Whether the image should fill its parent container
   * When true, width and height are ignored
   * @default false
   */
  fill?: boolean;

  /**
   * Sizes attribute for responsive images
   * @example "(max-width: 768px) 100vw, 50vw"
   */
  sizes?: string;

  /**
   * Whether this image should be prioritized for loading (above-the-fold images)
   * @default false
   */
  priority?: boolean;

  /**
   * Whether to include a preload link for this image in the head
   * Useful for critical images that should start loading immediately
   * @default false
   */
  preload?: boolean;

  /**
   * Image quality (1-100)
   * @default 75
   */
  quality?: number;

  /**
   * Additional CSS classes
   */
  className?: string;

  /**
   * Loading strategy
   * @default "lazy"
   */
  loading?: "lazy" | "eager";

  /**
   * Placeholder behavior while loading
   * @default "empty"
   */
  placeholder?: "blur" | "empty";

  /**
   * Blur data URL for placeholder (base64 encoded)
   * Only works when placeholder="blur"
   */
  blurDataURL?: string;

  /**
   * Callback when image loads successfully
   */
  onLoad?: () => void;

  /**
   * Callback when image fails to load
   */
  onError?: () => void;

  /**
   * Focal point for smart cropping (from CMS like Directus)
   * Overrides objectPosition variant when provided
   * @example { x: 75, y: 25 } - Focus on right-top area
   */
  focalPoint?: FocalPoint;

  /**
   * Position of this image within its lightbox group. Setting it makes the
   * image open fullscreen on click, navigable to the other images of the group.
   *
   * Requires a `<LightboxProvider>` ancestor and — like onLoad/onError — a
   * client component as the caller.
   */
  lightboxIndex?: number;
}

/**
 * Image component wrapper around Next.js Image with CVA variants
 *
 * Provides optimized image loading with support for:
 * - Automatic image optimization
 * - Responsive images via sizes attribute
 * - Lazy loading by default
 * - Object-fit and positioning variants
 * - Border radius variants
 *
 * @example
 * ```tsx
 * // Fixed dimensions
 * <Image
 *   src="/images/hero.jpg"
 *   alt="Hero image"
 *   width={1200}
 *   height={600}
 *   priority
 * />
 *
 * // Fill container
 * <Image
 *   src="/images/background.jpg"
 *   alt="Background"
 *   fill
 *   objectFit="cover"
 *   sizes="100vw"
 * />
 *
 * // Responsive with variants
 * <Image
 *   src="/images/card.jpg"
 *   alt="Card image"
 *   width={400}
 *   height={300}
 *   sizes="(max-width: 768px) 100vw, 400px"
 *   objectFit="cover"
 *   rounded="lg"
 * />
 *
 * // With focal point (from Directus)
 * <Image
 *   src="/images/portrait.jpg"
 *   alt="Portrait"
 *   fill
 *   objectFit="cover"
 *   focalPoint={{ x: 75, y: 25 }}
 * />
 * ```
 */
export const Image: React.FC<ImageProps> = t0 => {
  const $ = _c(30);
  const {
    src,
    alt,
    width,
    height,
    fill: t1,
    sizes,
    priority: t2,
    preload: t3,
    quality: t4,
    className,
    loading,
    placeholder: t5,
    blurDataURL,
    onLoad,
    onError,
    objectFit,
    objectPosition,
    rounded,
    focalPoint,
    lightboxIndex
  } = t0;
  const fill = t1 === undefined ? false : t1;
  const priority = t2 === undefined ? false : t2;
  const preload = t3 === undefined ? false : t3;
  const quality = t4 === undefined ? 75 : t4;
  const placeholder = t5 === undefined ? "empty" : t5;
  let t6;
  if ($[0] !== objectFit || $[1] !== objectPosition || $[2] !== rounded) {
    t6 = imageVariants({
      objectFit,
      objectPosition,
      rounded
    });
    $[0] = objectFit;
    $[1] = objectPosition;
    $[2] = rounded;
    $[3] = t6;
  } else {
    t6 = $[3];
  }
  const variantClasses = t6;
  const combinedClassName = className ? `${variantClasses} ${className}` : variantClasses;
  let t7;
  if ($[4] !== focalPoint || $[5] !== height || $[6] !== width) {
    t7 = focalPoint ? {
      objectPosition: focalPointToPosition(focalPoint, {
        width: width || 0,
        height: height || 0
      })
    } : undefined;
    $[4] = focalPoint;
    $[5] = height;
    $[6] = width;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  const objectPositionStyle = t7;
  const t8 = fill ? undefined : width;
  const t9 = fill ? undefined : height;
  let t10;
  if ($[8] !== alt || $[9] !== blurDataURL || $[10] !== combinedClassName || $[11] !== fill || $[12] !== loading || $[13] !== objectPositionStyle || $[14] !== onError || $[15] !== onLoad || $[16] !== placeholder || $[17] !== preload || $[18] !== priority || $[19] !== quality || $[20] !== sizes || $[21] !== src || $[22] !== t8 || $[23] !== t9) {
    t10 = {
      src,
      alt,
      width: t8,
      height: t9,
      fill,
      sizes,
      priority,
      preload,
      quality,
      className: combinedClassName,
      style: objectPositionStyle,
      loading,
      placeholder,
      blurDataURL,
      onLoad,
      onError
    };
    $[8] = alt;
    $[9] = blurDataURL;
    $[10] = combinedClassName;
    $[11] = fill;
    $[12] = loading;
    $[13] = objectPositionStyle;
    $[14] = onError;
    $[15] = onLoad;
    $[16] = placeholder;
    $[17] = preload;
    $[18] = priority;
    $[19] = quality;
    $[20] = sizes;
    $[21] = src;
    $[22] = t8;
    $[23] = t9;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  const imageProps = t10;
  if (lightboxIndex !== undefined) {
    let t11;
    if ($[25] !== imageProps || $[26] !== lightboxIndex) {
      t11 = <LightboxImage {...imageProps} lightboxIndex={lightboxIndex} />;
      $[25] = imageProps;
      $[26] = lightboxIndex;
      $[27] = t11;
    } else {
      t11 = $[27];
    }
    return t11;
  }
  let t11;
  if ($[28] !== imageProps) {
    t11 = <NextImage {...imageProps} />;
    $[28] = imageProps;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  return t11;
};